import { DOMAIN } from "../constants";
import { IDevelopmentProject } from "../interfaces/development-project.interface";

const S3_CS = 'https://semnexus-blog-media.s3.us-east-1.amazonaws.com/case-studies';

export const DEV_PROJECTS: IDevelopmentProject[] = [
    {
        name: 'Cerebyte',
        logo: `${S3_CS}/cerebyte/logo.png`,
        description: 'Cerebyte is a wellness mobile app that plays audio tracks derived from real EEG brain-activity recordings — focus, calm, deep sleep, creative flow, and more. Members browse a library of recordings, build playlists, and use a Wake Up alarm that rises to a chosen track.',
        redirectUrl: 'cerebyte',
        images: [
            `${S3_CS}/cerebyte/01.png`,
            `${S3_CS}/cerebyte/02.png`,
            `${S3_CS}/cerebyte/03.png`,
            `${S3_CS}/cerebyte/04.png`,
            `${S3_CS}/cerebyte/05.png`,
        ],
        discovery: 'The client came to SEM Nexus to bring a clinically-grounded EEG-to-audio process to a consumer mobile app. The work centered on packaging a scientifically-derived audio library into a polished cross-platform listening experience, with subscription gating, content discovery, and the audio-fidelity demands that ambient/wellness listeners expect.',
        design: 'The app is organized into themed "Labs" — Deep Sleep, Baby Sleep, and more — each holding curated audio tracks. A persistent mini-player keeps audio controls available across the app, a Wake Up alarm lets users rise to a chosen track, and saved Sessions and Favorites give listeners a way back to what works for them.',
        development: 'SEM Nexus built the front-end with Flutter, shipping a single performant codebase to iOS and Android with the audio fidelity the product depends on. A Node.js back-end powers the audio library, subscription state, sequence storage, and content delivery.',
        downloadLinks: {
            google: 'https://play.google.com/store/apps/details?id=com.cerebyte.android',
            apple: 'https://apps.apple.com/us/app/cerebyte/id6760419256',
        },
    },
    {
        name: 'Big Balls Brotherhood',
        logo: `${S3_CS}/big-balls-brotherhood/logo.png`,
        description: 'Big Balls Brotherhood is the mobile platform for a Christian men\'s accountability program. Members start each day with a check-in, set goals across three pillars — Wealth, Strength, Leadership — and post inside a community forum that ties the digital experience to the broader Brotherhood curriculum.',
        redirectUrl: 'big-balls-brotherhood',
        images: [
            `${S3_CS}/big-balls-brotherhood/01.png`,
            `${S3_CS}/big-balls-brotherhood/02.png`,
            `${S3_CS}/big-balls-brotherhood/03.png`,
            `${S3_CS}/big-balls-brotherhood/04.png`,
            `${S3_CS}/big-balls-brotherhood/05.png`,
        ],
        discovery: 'Founder Daniel Krynzel came to SEM Nexus with a movement already in motion — weekly accountability calls, quarterly events, Systems Training — and a need for the digital backbone to carry the discipline into members\' daily lives, end the isolation between meetings, and turn intention into a visible daily rhythm.',
        design: 'The app is organized around a five-tab structure — Home, Forum, Education, Plan, Tasks. Members open to a daily check-in and journal feed, plan their lives across three time horizons (3-Year Visions, Monthly Goals, Weekly Commitments), track those commitments by pillar (Wealth/Strength/Leadership), and post inside a forum threaded by topic.',
        development: 'Built with Flutter, shipping a single codebase to iOS and Android with the visual polish to match the movement\'s bold gold-on-black brand. A shared backend powers authentication, content delivery, journal entries, commitment tracking, and the community forum.',
        downloadLinks: {
            google: 'https://play.google.com/store/apps/details?id=com.semnexus.apps.big_balls_brotherhood',
            apple: 'https://apps.apple.com/us/app/big-balls-brotherhood/id6749697958',
        },
    },
    {
        name: 'MyPace',
        logo: `${S3_CS}/mypace/logo.png`,
        description: 'MyPace is a fitness and wellness mobile app that brings workouts, nutrition tracking, habit-building, and community into a single experience. AI-generated weekly workout routines and meal plans adapt to each member\'s goals, preferences, and activity level over time.',
        redirectUrl: 'mypace',
        images: [
            `${S3_CS}/mypace/01.png`,
            `${S3_CS}/mypace/02.png`,
            `${S3_CS}/mypace/03.png`,
            `${S3_CS}/mypace/04.png`,
            `${S3_CS}/mypace/05.png`,
        ],
        discovery: 'MyPace came to SEM Nexus needing to turn an AI-driven wellness concept into a polished cross-platform mobile app. The core problem to solve was personalization at scale — generating workout and meal plans that felt individually crafted, then adapting them as each member\'s data came in.',
        design: 'The app has a five-tab structure — Fitness, Nutrition, Home, Wellness, Connect — that gives each pillar its own surface. Members track macros and hydration, browse a workout library categorized by goal (cardio, strength, home/quick, fat loss), discover recipes and meal galleries, and share progress in a community feed with photos, likes, and comments.',
        development: 'SEM Nexus built the front-end with Angular and Ionic, shipping a single codebase to iOS and Android. A Node.js back-end powers the AI plan generation, member data, logging, and the adaptive recalibration loop that updates plans as activity and goals shift.',
        downloadLinks: {
            google: 'https://play.google.com/store/apps/details?id=app.mypace.com',
            apple: 'https://apps.apple.com/us/app/mypace-app/id6745795729',
        },
    },
    {
        name: '360 Medical Consulting',
        logo: `${S3_CS}/360-medical-consulting/logo.png`,
        description: '360 Medical Consulting is a HIPAA-compliant patient portal app that lets patients of a private medical practice schedule appointments, message their care team, and manage their health profile securely from their phone.',
        redirectUrl: '360-medical-consulting',
        images: [
            `${S3_CS}/360-medical-consulting/01.png`,
            `${S3_CS}/360-medical-consulting/02.png`,
            `${S3_CS}/360-medical-consulting/03.png`,
            `${S3_CS}/360-medical-consulting/04.png`,
            `${S3_CS}/360-medical-consulting/05.png`,
        ],
        discovery: 'The client, a private medical practice, came to SEM Nexus to extend their care experience to a mobile app. They wanted patients to handle appointments, billing, and messaging on their own — freeing up the front desk while keeping every interaction HIPAA-compliant.',
        design: 'The app is organized around four areas: appointment scheduling, secure messaging with the practice and doctors, a personal health profile (medical history, allergies, medications, insurance), and notifications. The interface is clean and large-type-friendly, with short paths from home to any task.',
        development: 'The app was built with Angular and Ionic, shipping a single TypeScript codebase to iOS and Android. HIPAA-compliant encryption and access controls were enforced at every layer.',
        downloadLinks: {
            google: 'https://play.google.com/store/apps/details?id=com.medical360.consulting',
            apple: 'https://apps.apple.com/us/app/360-medical-consulting/id6596748340',
        },
    },
    {
        name: 'My Home Delivery',
        logo: `${S3_CS}/my-home-delivery/logo.png`,
        description: 'My Home Delivery is the on-demand mobile platform for big-and-bulky item delivery — furniture, appliances, and large retail purchases. Same-day or scheduled service with instant upfront pricing, real-time tracking, and a network of vetted, insured carriers.',
        redirectUrl: 'my-home-delivery',
        images: [
            `${S3_CS}/my-home-delivery/01.png`,
            `${S3_CS}/my-home-delivery/02.png`,
            `${S3_CS}/my-home-delivery/03.png`,
            `${S3_CS}/my-home-delivery/04.png`,
            `${S3_CS}/my-home-delivery/05.png`,
        ],
        discovery: 'The client identified a gap in last-mile delivery: traditional freight isn\'t built for same-day consumer service, and gig-economy platforms cap out on item size. The brief was a rideshare-style experience — upfront pricing, real-time tracking, vetted insured carriers — built for items that need a real truck and a crew.',
        design: 'The app centers on a tight three-step flow: Get Estimate, Book, Track. Pickup and drop-off entry surfaces a locked-in price with no hidden fees; booking adds details for big items (elevator vs. stairs, item count, photos). Real-time tracking, carrier messaging, and Apple Wallet support carry the customer through delivery.',
        development: 'The customer app was built with React Native on the front-end and a Laravel back-end powering the marketplace\'s APIs, pricing engine, carrier dispatch, and order state — iterated across more than a dozen versioned releases.',
        downloadLinks: {
            google: 'https://play.google.com/store/apps/details?id=com.mhd.user',
            apple: 'https://apps.apple.com/us/app/my-home-delivery/id1588425069',
        },
    },
    {
        name: 'CellMyListing',
        logo: `${DOMAIN}wp-content/uploads/2023/11/icon-4-300x300.png`,
        description: 'This reliable all-in-one app provides you with the essential features to take stunning pictures of a property just by using your cellphone’s camera. It allows users to order different types of services to enhance their pictures so they can promote their properties on the MLS and other apps and websites.',
        redirectUrl: 'cellmylisting',
        images: [
            `${DOMAIN}wp-content/uploads/2023/11/01-5.png`,
            `${DOMAIN}wp-content/uploads/2023/11/02-6.png`,
            `${DOMAIN}wp-content/uploads/2023/11/03-6.png`,
            `${DOMAIN}wp-content/uploads/2023/11/04-5.png`,
            `${DOMAIN}wp-content/uploads/2023/11/splash-4.png`
        ],
        background: `${DOMAIN}wp-content/uploads/2023/11/bg-3.png`,
        discovery: 'Real estate professionals understand the vital importance of high-quality photos when selling property.',
        design: 'Free tutorials that guide you step-by-step on taking near-professional-quality real estate pictures on your cell phone. Visually appealing and strategically designed to captivate potential buyers',
        development: 'The app was built using Flutter, and the database runs on Google’s Firebase database.'
    },
    {
        name: 'Cheersmate',
        logo: `${DOMAIN}wp-content/uploads/2023/11/icon-3-300x300.png`,
        description: 'Allows users to order drinks from their app and pick them up at the bar. Customers can run a tab and settle it, tip, and close their tab from their phones.',
        redirectUrl: 'cheersmate',
        images: [
            `${DOMAIN}wp-content/uploads/2023/11/01-4.png`,
            `${DOMAIN}wp-content/uploads/2023/11/02-5.png`,
            `${DOMAIN}wp-content/uploads/2023/11/03-5.png`,
            `${DOMAIN}wp-content/uploads/2023/11/04-4.png`,
            `${DOMAIN}wp-content/uploads/2023/11/splash-3.png`
        ],
        background: `${DOMAIN}wp-content/uploads/2023/11/bg-2.png`,
        discovery: 'The client came to us trying to solve the problem of ordering drinks without having to get a waiter’s attention or ahead of arriving at their local bar.',
        design: 'The app features a version for the client where the bars around them are displayed, and they can open a tab and order drinks without even being there.',
        development: 'The app was built using Flutter, and the database runs on Google’s Firebase database.',
        downloadLinks: {
            google: 'https://play.google.com/store/apps/details?id=com.semnexus.apps.cheersmate&pcampaignid=web_share',
            apple: ''
        }
    },
    {
        name: 'LEW',
        logo: `${DOMAIN}wp-content/uploads/2023/11/icon-2-300x300.png`,
        description: 'Lew was designed to help with dementia loved ones and their caregivers. We designed Lew with three aspects: the loved one (the person with dementia or needing help), the caregiver, and friends and family.',
        redirectUrl: 'lew',
        images: [
            `${DOMAIN}wp-content/uploads/2023/11/01-3.png`,
            `${DOMAIN}wp-content/uploads/2023/11/02-4.png`,
            `${DOMAIN}wp-content/uploads/2023/11/03-4.png`,
            `${DOMAIN}wp-content/uploads/2023/11/04-3.png`,
            `${DOMAIN}wp-content/uploads/2023/11/splash-2.png`
        ],
        background: `${DOMAIN}wp-content/uploads/2023/11/bg-1.png`,
        discovery: 'The client came to us with an idea on how to keep in touch with loved ones who were living in assisted living or away from their loved ones. Easy to use for people with dementia or age-related disabilities.',
        design: `Daily Checklist,  Daily Calendar Pictures of People and Places.
                    Send messages directly to loved ones you know who are suffering from dementia.`,
        development: 'The app was built using Flutter, and the database runs on Google’s Firebase database.',
        downloadLinks: {
            google: 'https://play.google.com/store/apps/details?id=com.lewapp.android&pcampaignid=web_share',
            apple: 'https://apps.apple.com/us/app/lew-app/id6447646984'
        }
    },
    {
        name: 'Boss App',
        logo: `${DOMAIN}wp-content/uploads/2023/11/icon-300x300.png`,
        description: 'Boss is designed to be the ultimate app to transform how you find work and workers. Whether you’re looking for opportunities to utilize your skills and free time or need the ideal person to get a job done, Boss is your one-stop solution. With options for both English and Spanish, Boss offers additional accessibility and opportunities for collaboration.',
        redirectUrl: 'boss',
        images: [
            `${DOMAIN}wp-content/uploads/2023/11/01-1.png`,
            `${DOMAIN}wp-content/uploads/2023/11/02-2.png`,
            `${DOMAIN}wp-content/uploads/2023/11/03-2.png`,
            `${DOMAIN}wp-content/uploads/2023/11/04-1.png`,
            `${DOMAIN}wp-content/uploads/2023/11/splash.png`
        ],
        background: `${DOMAIN}wp-content/uploads/2023/11/bg.jpg`,
        discovery: 'The client realized an unattended niche in their area, so they approached Sem Nexus with an idea and a concept. During the discovery phase, the app took shape, and the design was completed.',
        design: `The app’s design is meant to make it easy for people looking for a job and those who need to find skilled labor in their particular area. It was decided that the app should be published in Spanish and English to cover more people.`,
        development: 'The app is coded in Angular/Ionic, and the database is PostgreSQL.',
        downloadLinks: {
            apple: 'https://apps.apple.com/us/app/a-boss-app/id1671890647',
            google: ''
        }
    },
    {
        name: 'HomeVetNow User',
        logo: `${DOMAIN}wp-content/uploads/2023/04/homevetnow_user_icon-300x300.png`,
        description: 'HomeVetNow User provides pet owners with real-time access to vetted and certified veterinarians in their area. Users can browse profiles, view qualifications, read reviews, and connect with their preferred pet care providers directly through the app, ensuring peace of mind and top-notch care for their pets.',
        redirectUrl: 'homevetnow-user',
        images: [
            `${DOMAIN}wp-content/uploads/2023/04/homevetnow_user_01.png`,
            `${DOMAIN}wp-content/uploads/2023/04/homevetnow_user_02.png`,
            `${DOMAIN}wp-content/uploads/2023/04/homevetnow_user_03.png`,
            `${DOMAIN}wp-content/uploads/2023/04/homevetnow_user_04.png`,
            `${DOMAIN}wp-content/uploads/2023/04/homevetnow_user_splash.png`
        ],
        background: `${DOMAIN}wp-content/uploads/2023/04/homevetnow_user_bg.jpg`,
        discovery: 'HomeVetNow User was developed to offer pet owners the convenience and assurance of quality care at their fingertips.',
        design: `The app offers an intuitive interface that allows users to easily manage their pets' information, personal profile, and follow-up care.'`,
        development: 'SEM Nexus developed HomeVetNow utilizing Flutter.'
    },
    {
        name: "Truck'N",
        description: "Truck'N is a comprehensive app designed to help truckers stay organized and efficient on the road. With features like load accounting, smart GPS, en-route reports, broker reviews, and a dedicated social platform, truckers can easily manage their journeys and connect with others in the industry.",
        logo: `${DOMAIN}wp-content/uploads/2023/04/truckn_icon-300x300.jpg`,
        images: [
            `${DOMAIN}wp-content/uploads/2023/04/truckn_01.png`,
            `${DOMAIN}wp-content/uploads/2023/04/truckn_02.png`,
            `${DOMAIN}wp-content/uploads/2023/04/truckn_03.png`,
            `${DOMAIN}wp-content/uploads/2023/04/truckn_04.png`,
            `${DOMAIN}wp-content/uploads/2022/11/truckn_splash.png`
        ],
        redirectUrl: "truckn",
        background: `${DOMAIN}wp-content/uploads/2023/04/truckn_bg.jpg`,
        discovery: `Truck'N was developed to provide truckers with essential tools and resources, ensuring a more enjoyable and efficient experience on the road.`,
        design: `The app offers an intuitive interface that allows users to seamlessly navigate between features, such as load accounting, smart GPS, and en-route reports.`,
        development: `SEM Nexus developed Truck'N utilizing Flutter.`
    },
    {
        name: "Trusted Service",
        description: "Trusted Services is a convenient app that allows users to schedule and pay for service appointments with professionals such as Handymen, Electricians, Locksmiths, and Plumbers Trusted Services ensures quality and reliability!",
        logo: `${DOMAIN}wp-content/uploads/2022/11/icon-1-300x300.png`,
        images: [
            `${DOMAIN}wp-content/uploads/2023/04/trusted_01.png`,
            `${DOMAIN}wp-content/uploads/2023/04/trusted_02.png`,
            `${DOMAIN}wp-content/uploads/2023/04/trusted_03.png`,
            `${DOMAIN}wp-content/uploads/2023/04/trusted_04.png`,
            `${DOMAIN}wp-content/uploads/2022/10/trustedservices-sticky-506x1024.png`
        ],
        redirectUrl: "trusted-services",
        background: `${DOMAIN}wp-content/uploads/2023/04/trusted_bg.jpg`,
        discovery: 'The discovery process for the app began when the client recognized the need for a platform that simplifies the process of finding and hiring trusted service professionals.',
        design: 'The app offers an intuitive interface that enables users to effortlessly browse services, compare prices, and schedule appointments.',
        development: 'SEM Nexus Developed Trusted Services utilizing Flutter.'
    },
    {
        name: "Yogi-Go Fitness",
        description: "The Yogi-Go Fitness app is your premier app for discovering, messaging, and booking appointments with nearby yoga/fitness instructors and personal trainers. It’s also a great way for trainers to be found by potential clients!",
        logo: `${DOMAIN}wp-content/uploads/2022/11/icon.png`,
        images: [
            `${DOMAIN}wp-content/uploads/2023/04/yogigo_01.png`,
            `${DOMAIN}wp-content/uploads/2023/04/yogigo_02.png`,
            `${DOMAIN}wp-content/uploads/2023/04/yogigo_03.png`,
            `${DOMAIN}wp-content/uploads/2023/04/yogigo_04.png`,
            `${DOMAIN}wp-content/uploads/2022/10/yogigosticky-1-506x1024.png`
        ],
        redirectUrl: "yogigo-fitness",
        background: `${DOMAIN}wp-content/uploads/2022/10/yogigo_bg.jpg`,
        discovery: 'The discovery process for the app began when the client recognized the need for a user-friendly platform to connect fitness enthusiasts with qualified trainers, yogis, and instructors.',
        design: 'The app provides an intuitive user interface, making it simple for users to find and communicate with fitness professionals, ensuring a seamless booking experience.',
        development: 'SEM Nexus Developed Yogi-Go Fitness utilizing Flutter.'
    }
];